/**
 * Admin home — mobile: greeting band + KPI tile grid + next-approvals
 * preview + quick-create actions (redesign_v3 "Admin home mobile" frame).
 * Desktop (≥1024, web): full console dashboard — sidebar nav, KPI row,
 * live approvals queue, quick links ("Admin dashboard" frame).
 * Hard role guard (SUB_ADMIN / MASTER_ADMIN only), unchanged.
 * Wired: GET /api/admin/overview, GET /api/admin/approvals,
 * POST /api/admin/approvals/<id>. Port of screens-admin.jsx AdminHome.
 *
 * DEVIATION: the design's desktop "Collections" (billing %) and "Needs
 * moderation" panels have no backing endpoint (apps/api/app/blueprints/admin.py
 * exposes no aggregate billing or generic moderation-queue route) — replaced
 * with a live approvals queue + a real quick-links panel instead of inventing
 * numbers. The single sanctioned amber moment per screen is the highlighted
 * "Pending approvals" KPI/tile — nothing else on either layout uses accent.
 */
import { router } from 'expo-router';
import React from 'react';
import { Platform, Pressable, View, useWindowDimensions } from 'react-native';

import { radius, space } from '@bero/tokens';
import { num, str, useGateList } from '@/components/gate/api';
import { AdminSidebar, goBack, KpiCard, SmallButton } from '@/features/account-admin/bits';
import { Icon, type IconName } from '@/features/account-admin/icons';
import { useActingSociety } from '@/lib/acting-society';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, Avatar, Button, Card, EmptyState, Pill, Screen, Skeleton, TopBar } from '@/ui';

const WIDE = 1024;

type Counts = Record<string, number | undefined>;

type Tile = [label: string, icon: IconName, path: string, countKey: string];

/** Grouped like the sidebar (user report 2026-07-18: flat grid was unscannable). */
const TILE_GROUPS: { title: string; tiles: Tile[] }[] = [
  {
    title: 'People',
    tiles: [
      ['Pending approvals', 'user-plus', '/admin/approvals', 'pendingApprovals'],
      ['Users', 'users', '/admin/users', 'users'],
    ],
  },
  {
    title: 'Community',
    tiles: [
      ['Notices', 'megaphone', '/admin/notices', 'notices'],
      ['Polls', 'list', '/admin/polls', 'polls'],
      ['Issues', 'alert-circle', '/admin/issues', 'issues'],
      ['Vendors', 'wrench', '/admin/vendors', 'vendors'],
      ['Amenities', 'calendar', '/admin/amenities', 'amenities'],
    ],
  },
  {
    title: 'Governance',
    tiles: [
      ['Positions', 'key', '/admin/positions', 'positions'],
      ['Meetings', 'calendar', '/meetings', 'meetings'],
      ['Compliance', 'alert-circle', '/compliance', 'complianceDue'],
      ['Elections', 'check', '/elections', 'electionsActive'],
    ],
  },
  {
    title: 'Oversight',
    tiles: [
      ['Pending actions', 'clock', '/pending-actions', 'pendingActions'],
      ['Audit trail', 'file-text', '/admin/audit', 'audit'],
    ],
  },
];
const TILES: Tile[] = TILE_GROUPS.flatMap((g) => g.tiles);

const QUICK_CREATE: Array<[label: string, path: string]> = [
  ['+ Notice', '/admin/create-notice'],
  ['+ Poll', '/admin/create-poll'],
  ['+ Event', '/events/create'],
  ['+ Vendor', '/admin/create-vendor'],
  ['+ Amenity', '/admin/create-amenity'],
];

function parseCounts(body: unknown): Counts {
  const src = (body && typeof body === 'object'
    ? ((body as Record<string, unknown>).counts ?? body)
    : {}) as Record<string, unknown>;
  return {
    pendingApprovals: num(src, 'pendingApprovals', 'approvals', 'pending'),
    users: num(src, 'users', 'userCount', 'activeUsers'),
    notices: num(src, 'notices', 'noticeCount'),
    polls: num(src, 'polls', 'pollCount', 'activePolls'),
    issues: num(src, 'issues', 'issueCount', 'openIssues'),
    vendors: num(src, 'vendors', 'vendorCount'),
    amenities: num(src, 'amenities', 'amenityCount'),
    positions: num(src, 'positions', 'positionCount'),
    pendingActions: num(src, 'pendingActions', 'pendingActionCount'),
    audit: num(src, 'audit', 'auditCount'),
    meetings: num(src, 'meetings', 'meetingCount'),
    complianceDue: num(src, 'complianceDue'),
    electionsActive: num(src, 'electionsActive'),
  };
}

type PendingRow = { id: string; name: string; flat: string; role: string };

function flatCodeOf(u: unknown): string {
  const f = (u as { flat?: { code?: string } })?.flat;
  return (f && typeof f.code === 'string' ? f.code : str(u, 'flatCode')) ?? '';
}

function toPending(u: unknown, i: number): PendingRow {
  return {
    // Approvals are membership requests now: the row id IS the membership id.
    id: str(u, 'membershipId', 'id', 'userId') ?? `u${i}`,
    name: str(u, 'userName', 'name', 'fullName') ?? 'Resident',
    flat: flatCodeOf(u),
    role: str(u, 'residentType', 'role') ?? '',
  };
}

export default function AdminHomeScreen() {
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const wide = Platform.OS === 'web' && width >= WIDE;
  const { user, loading: sessionLoading } = useSession();
  const acting = useActingSociety();
  // Mirrors AdminGate.tsx: an acting PLATFORM_ADMIN reuses this console with
  // no role/token swap, so the overview fetch and gate both need the same
  // acting-society exception (never granted while not acting).
  const isAdmin = user?.role === 'SUB_ADMIN' || user?.role === 'MASTER_ADMIN'
    || (user?.role === 'PLATFORM_ADMIN' && !!acting);

  const [counts, setCounts] = React.useState<Counts>({});
  const [loading, setLoading] = React.useState(true);
  const { items: pendingItems, state: pendingState } = useGateList<unknown>(
    '/api/admin/approvals', 'approvals', 'pending', 'users',
  );
  const pending = pendingItems.map(toPending);

  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [decided, setDecided] = React.useState<Record<string, true>>({});

  React.useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const body = await api.get<unknown>('/api/admin/overview');
        if (!cancelled) setCounts(parseCounts(body));
      } catch {
        // Overview endpoint not live yet — tiles still navigate.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  const actOnApproval = (row: PendingRow, action: 'approve' | 'reject') => {
    setBusyId(row.id);
    api.post<unknown>(`/api/admin/approvals/${row.id}`, { action })
      .then(() => setDecided((s) => ({ ...s, [row.id]: true })))
      .catch(() => {})
      .finally(() => setBusyId(null));
  };

  if (!sessionLoading && !isAdmin) {
    return (
      <Screen scroll={false}>
        <TopBar title="Admin" onBack={goBack} />
        <EmptyState
          icon={<Icon name="shield" size={40} color={colors.textTertiary} />}
          title="Admins only"
          body="This area is for society committee admins. Ask your committee if you think you should have access."
        />
      </Screen>
    );
  }

  const visiblePending = pending.filter((p) => !decided[p.id]);

  /* ---------------- desktop console dashboard ---------------- */
  if (wide) {
    return (
      <View style={{ flex: 1, flexDirection: 'row', backgroundColor: colors.canvas0 }}>
        <AdminSidebar counts={counts} />
        <Screen padded style={{ paddingTop: space.s6, paddingHorizontal: space.s8 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: space.s6 }}>
            <View style={{ flex: 1 }}>
              <AppText variant="headingLg">Society overview</AppText>
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                {user?.role === 'MASTER_ADMIN' ? 'Master admin · full access' : 'Committee admin'}
              </AppText>
            </View>
            <Button title="+ New notice" onPress={() => router.push('/admin/create-notice')} />
          </View>

          {loading ? (
            <View style={{ flexDirection: 'row', gap: space.s4 }}>
              {TILES.map(([label]) => <Skeleton key={label} h={92} style={{ flex: 1 }} />)}
            </View>
          ) : (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s4 }}>
              {TILES.map(([label, , path, countKey]) => (
                <Pressable key={label} onPress={() => router.push(path)} style={{ flexGrow: 1, minWidth: 150 }}>
                  <KpiCard
                    label={label}
                    value={counts[countKey] !== undefined ? String(counts[countKey]) : '—'}
                    highlight={countKey === 'pendingApprovals' && !!counts[countKey]}
                  />
                </Pressable>
              ))}
            </View>
          )}

          <View style={{ flexDirection: 'row', gap: space.s5, marginTop: space.s5 }}>
            <Card style={{ flex: 1.3 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: space.s3 }}>
                <AppText variant="label" tone="tertiary">Approvals queue · {visiblePending.length}</AppText>
                <Pressable onPress={() => router.push('/admin/approvals')} style={{ marginLeft: 'auto' }}>
                  <AppText variant="bodySm" weight="600" tone="brand">Open queue →</AppText>
                </Pressable>
              </View>
              {pendingState === 'loading' ? (
                <View style={{ gap: space.s2 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={56} />)}</View>
              ) : visiblePending.length === 0 ? (
                <AppText variant="bodySm" tone="tertiary">No pending requests right now.</AppText>
              ) : (
                visiblePending.slice(0, 5).map((p) => (
                  <View key={p.id} style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: space.s2 }}>
                    <Avatar name={p.name} size={38} />
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <AppText variant="bodyMd" weight="600">{p.name}</AppText>
                      <AppText variant="bodySm" tone="tertiary">{[p.flat, p.role].filter(Boolean).join(' · ')}</AppText>
                    </View>
                    <SmallButton label="Approve" disabled={busyId === p.id} onPress={() => actOnApproval(p, 'approve')} />
                    <SmallButton label="Reject" danger disabled={busyId === p.id} onPress={() => actOnApproval(p, 'reject')} />
                  </View>
                ))
              )}
            </Card>
            <Card style={{ flex: 1 }}>
              {TILE_GROUPS.map((grp, gi) => (
                <View key={grp.title}>
                  <AppText variant="label" tone="tertiary"
                    style={{ marginBottom: 4, marginTop: gi > 0 ? space.s3 : 0 }}>
                    {grp.title}
                  </AppText>
                  {grp.tiles
                    .filter(([, , path]) => path !== '/admin/approvals')  // queue card sits beside this panel
                    .map(([label, icon, path, countKey]) => (
                      <Pressable
                        key={label}
                        onPress={() => router.push(path)}
                        style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: 8 }}
                      >
                        <Icon name={icon} size={18} color={colors.textSecondary} />
                        <AppText variant="bodyMd" style={{ flex: 1 }}>{label}</AppText>
                        <AppText variant="bodySm" tone="tertiary" mono>{counts[countKey] ?? '—'}</AppText>
                      </Pressable>
                    ))}
                </View>
              ))}
            </Card>
          </View>
        </Screen>
      </View>
    );
  }

  /* ---------------- mobile ---------------- */
  return (
    <Screen scroll={false}>
      <TopBar title="Admin" onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s2 }}>
        <View style={{ borderRadius: radius.xl, backgroundColor: colors.canvas1, padding: space.s4, marginBottom: space.s4 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
            <AppText variant="headingMd" weight="600" style={{ flex: 1 }}>
              {user?.fullName ? `Hello, ${user.fullName.split(' ')[0]}` : 'Society admin'}
            </AppText>
            <Pill status="pending" label={user?.role === 'MASTER_ADMIN' ? 'MASTER ADMIN' : 'ADMIN'} />
          </View>
          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
            {(counts.pendingApprovals ?? 0) > 0
              ? `${counts.pendingApprovals} item${counts.pendingApprovals === 1 ? '' : 's'} need your attention`
              : 'All caught up'}
          </AppText>
        </View>

        {loading ? (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', rowGap: space.s3 }}>
            {TILES.map(([label]) => <Skeleton key={label} h={104} w="48%" />)}
          </View>
        ) : (
          TILE_GROUPS.map((grp, gi) => (
            <View key={grp.title}>
              <AppText variant="label" tone="tertiary"
                style={{ marginTop: gi > 0 ? space.s4 : 0, marginBottom: space.s2 }}>
                {grp.title}
              </AppText>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', rowGap: space.s3 }}>
                {grp.tiles.map(([label, icon, path, countKey]) => {
                  const count = counts[countKey];
                  const isUrgent = countKey === 'pendingApprovals' && !!count;
                  return (
                    <Card
                      key={label}
                      onPress={() => router.push(path)}
                      style={isUrgent
                        ? { width: '48%', borderColor: colors.accent500, backgroundColor: colors.accent100 }
                        : { width: '48%' }}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Icon name={icon} size={20} color={isUrgent ? colors.accent700 : colors.textSecondary} />
                        {isUrgent ? <Pill status="urgent" label={String(count)} /> : null}
                      </View>
                      <AppText
                        variant="label"
                        style={{ marginTop: space.s3, color: isUrgent ? colors.accent700 : colors.textTertiary }}
                      >
                        {label}
                      </AppText>
                      <AppText mono weight="500" style={{ fontSize: 22, marginTop: 4 }}>
                        {count !== undefined ? count : '—'}
                      </AppText>
                    </Card>
                  );
                })}
                {grp.tiles.length % 2 === 1 ? <View style={{ width: '48%' }} /> : null}
              </View>
            </View>
          ))
        )}

        <View style={{ borderWidth: 1, borderColor: colors.borderSoft, borderRadius: radius.xl, padding: space.s4, marginTop: space.s4 }}>
          <AppText variant="label" tone="tertiary" style={{ marginBottom: space.s3 }}>Next approvals</AppText>
          {pendingState === 'loading' ? (
            <View style={{ gap: space.s2 }}>{[0, 1].map((i) => <Skeleton key={i} h={44} />)}</View>
          ) : visiblePending.length === 0 ? (
            <AppText variant="bodySm" tone="tertiary">No pending requests.</AppText>
          ) : (
            visiblePending.slice(0, 3).map((p) => (
              <Pressable
                key={p.id}
                onPress={() => router.push('/admin/approvals')}
                style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: 8 }}
              >
                <Avatar name={p.name} size={34} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <AppText variant="bodyMd" weight="600">{p.name}</AppText>
                  <AppText variant="bodySm" tone="tertiary">{[p.flat, p.role].filter(Boolean).join(' · ')}</AppText>
                </View>
                <AppText variant="bodySm" weight="600" tone="brand">Review</AppText>
              </Pressable>
            ))
          )}
        </View>

        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s3, marginTop: space.s4 }}>
          {QUICK_CREATE.map(([label, path]) => (
            <Button
              key={label}
              title={label}
              kind={label === '+ Notice' ? 'primary' : 'secondary'}
              style={{ width: '48%' }}
              onPress={() => router.push(path)}
            />
          ))}
        </View>
      </Screen>
    </Screen>
  );
}
